import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  buildScopeContext,
  EditableCollectionBinding,
  EditableSlotGroup,
  useCollectionBinding,
  useCollectionBindingOverrides,
  useEditMode,
  usePage,
  useProperty,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  curateGalleryPhotos,
  galleryPhotoSrc,
  groupGalleryByTab,
  hasCustomGalleryOrder,
  type GalleryTab,
} from '../utils/sortGallery';
import {
  resolveStockLifestylePhotos,
  sprinkleStockPhotos,
} from '../utils/stockLifestyleImages';

interface GalleryImage {
  image?: string | null;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string | null;
  /** Set when this tile is one of the editable company-stock lifestyle slots. */
  stockSlotIndex?: number;
}

function lightboxCaption(photo: GalleryImage): string {
  const description = (photo.description || '').trim();
  if (description) return description;
  return (photo.alt_text || '').trim();
}

function photoAlt(photo: GalleryImage): string {
  return photo.alt_text || photo.title || '';
}

const TABS: { key: GalleryTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'community', label: 'Community' },
  { key: 'apartment', label: 'Apartment' },
];

// Editable company-stock lifestyle slots — empty defaults fall back to the
// property-seeded pool at render time; editor VALUE overrides replace a tile.
const stockLifestyle1Def: SlotGroupDefinition = {
  label: 'Lifestyle Stock 1',
  description: 'Company lifestyle image sprinkled into the gallery. Replace to override for this property.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const stockLifestyle2Def: SlotGroupDefinition = {
  label: 'Lifestyle Stock 2',
  description: 'Company lifestyle image sprinkled into the gallery. Replace to override for this property.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const stockLifestyle3Def: SlotGroupDefinition = {
  label: 'Lifestyle Stock 3',
  description: 'Company lifestyle image sprinkled into the gallery. Replace to override for this property.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const stockLifestyle4Def: SlotGroupDefinition = {
  label: 'Lifestyle Stock 4',
  description: 'Company lifestyle image sprinkled into the gallery. Replace to override for this property.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const stockLifestyle5Def: SlotGroupDefinition = {
  label: 'Lifestyle Stock 5',
  description: 'Company lifestyle image sprinkled into the gallery. Replace to override for this property.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

function StockLifestyleTile({
  photo,
  stockSlotIndex,
  isEditMode,
  onOpen,
}: {
  photo: GalleryImage;
  stockSlotIndex: number;
  isEditMode: boolean;
  onOpen: () => void;
}) {
  const image = (
    <div
      className="h-full w-full"
      onClick={() => {
        // In edit mode, stock tiles open their slot group — skip lightbox.
        if (isEditMode) return;
        onOpen();
      }}
    >
      <VibesitesImage
        url={galleryPhotoSrc(photo)}
        alt={photoAlt(photo)}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
      />
    </div>
  );

  const shellClass =
    'block aspect-square overflow-hidden group cursor-pointer gallery-item';

  // groupPath must be a static string literal for the schema linter.
  switch (stockSlotIndex) {
    case 0:
      return (
        <EditableSlotGroup
          groupPath="gallery.stock_lifestyle_1"
          definition={stockLifestyle1Def}
          className={shellClass}
        >
          {image}
        </EditableSlotGroup>
      );
    case 1:
      return (
        <EditableSlotGroup
          groupPath="gallery.stock_lifestyle_2"
          definition={stockLifestyle2Def}
          className={shellClass}
        >
          {image}
        </EditableSlotGroup>
      );
    case 2:
      return (
        <EditableSlotGroup
          groupPath="gallery.stock_lifestyle_3"
          definition={stockLifestyle3Def}
          className={shellClass}
        >
          {image}
        </EditableSlotGroup>
      );
    case 3:
      return (
        <EditableSlotGroup
          groupPath="gallery.stock_lifestyle_4"
          definition={stockLifestyle4Def}
          className={shellClass}
        >
          {image}
        </EditableSlotGroup>
      );
    case 4:
      return (
        <EditableSlotGroup
          groupPath="gallery.stock_lifestyle_5"
          definition={stockLifestyle5Def}
          className={shellClass}
        >
          {image}
        </EditableSlotGroup>
      );
    default:
      return null;
  }
}

export function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GalleryTab>('all');

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });
  const stockSlot1 = useSlotGroup('gallery.stock_lifestyle_1', stockLifestyle1Def);
  const stockSlot2 = useSlotGroup('gallery.stock_lifestyle_2', stockLifestyle2Def);
  const stockSlot3 = useSlotGroup('gallery.stock_lifestyle_3', stockLifestyle3Def);
  const stockSlot4 = useSlotGroup('gallery.stock_lifestyle_4', stockLifestyle4Def);
  const stockSlot5 = useSlotGroup('gallery.stock_lifestyle_5', stockLifestyle5Def);
  const property = useProperty();
  const page = usePage();
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;
  const collectionOverrides = useCollectionBindingOverrides();
  const customOrder = hasCustomGalleryOrder(
    resolveScopedOverride(collectionOverrides, 'gallery.images', buildScopeContext(page)),
  );

  const curated = useMemo(() => {
    const photos = images && images.length > 0 ? images : [];
    // A saved editor order is the gallery; otherwise sequence a property tour.
    return customOrder ? groupGalleryByTab(photos) : curateGalleryPhotos(photos);
  }, [images, customOrder]);

  const displayByTab = useMemo(() => {
    const withSrc = (photos: GalleryImage[]) =>
      photos.filter((photo) => !!galleryPhotoSrc(photo));

    const all = withSrc(curated.all);
    const community = withSrc(curated.community);
    const apartment = withSrc(curated.apartment);

    // Editor-ordered galleries own their sequence — do not inject stock.
    if (customOrder) {
      return { all, community, apartment };
    }

    const seed =
      property?.multihub_property_id ||
      property?.property_name ||
      'pathfinder-lifestyle';
    const stock = resolveStockLifestylePhotos(seed, [
      { image: stockSlot1.image, alt: stockSlot1.alt },
      { image: stockSlot2.image, alt: stockSlot2.alt },
      { image: stockSlot3.image, alt: stockSlot3.alt },
      { image: stockSlot4.image, alt: stockSlot4.alt },
      { image: stockSlot5.image, alt: stockSlot5.alt },
    ]);

    // Sprinkle company lifestyle stock through All + Community; leave Apartment pure.
    return {
      all: sprinkleStockPhotos(all, stock, seed),
      community: sprinkleStockPhotos(community, stock, `${seed}:community`),
      apartment,
    };
  }, [
    curated,
    customOrder,
    property?.multihub_property_id,
    property?.property_name,
    stockSlot1.image,
    stockSlot1.alt,
    stockSlot2.image,
    stockSlot2.alt,
    stockSlot3.image,
    stockSlot3.alt,
    stockSlot4.image,
    stockSlot4.alt,
    stockSlot5.image,
    stockSlot5.alt,
  ]);

  const filteredPhotos = displayByTab[activeTab];
  // Use `let` (not `const … = {`) so the schema static extractor does not
  // pull this into the contentSchema eval context.
  let tabCounts = {
    all: displayByTab.all.length,
    community: displayByTab.community.length,
    apartment: displayByTab.apartment.length,
  };

  const lightboxOpen = selectedIndex !== null;
  const selectedPhoto = lightboxOpen ? filteredPhotos[selectedIndex] ?? null : null;
  const caption = selectedPhoto ? lightboxCaption(selectedPhoto) : '';

  const openLightboxAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= filteredPhotos.length) return;
      setSelectedIndex(index);
    },
    [filteredPhotos.length],
  );

  const goToPrev = useCallback(() => {
    if (selectedIndex === null || filteredPhotos.length <= 1) return;
    setSelectedIndex((selectedIndex - 1 + filteredPhotos.length) % filteredPhotos.length);
  }, [selectedIndex, filteredPhotos.length]);

  const goToNext = useCallback(() => {
    if (selectedIndex === null || filteredPhotos.length <= 1) return;
    setSelectedIndex((selectedIndex + 1) % filteredPhotos.length);
  }, [selectedIndex, filteredPhotos.length]);

  const closeLightbox = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  // Mobile swipe support — track touch start/end X to navigate the lightbox
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      const SWIPE_THRESHOLD = 50;
      if (deltaX < -SWIPE_THRESHOLD) goToNext();
      else if (deltaX > SWIPE_THRESHOLD) goToPrev();
    },
    [goToNext, goToPrev],
  );

  // Keyboard navigation: Escape to close, Arrow keys for prev/next
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToPrev, goToNext]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images" className="block">
          {/* Tab filters */}
          {tabCounts.all > 0 && (
            <div className="gallery-tabs">
              {TABS.map((tab) => {
                const count = tabCounts[tab.key];
                if (tab.key !== 'all' && count === 0) return null;
                return (
                  <button
                    key={tab.key}
                    className={`gallery-tab${activeTab === tab.key ? ' gallery-tab--active' : ''}`}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setSelectedIndex(null);
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Photo grid */}
          {filteredPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filteredPhotos.map((photo, index) => {
                if (photo.stockSlotIndex != null) {
                  return (
                    <StockLifestyleTile
                      key={`stock-${photo.stockSlotIndex}-${galleryPhotoSrc(photo)}`}
                      photo={photo}
                      stockSlotIndex={photo.stockSlotIndex}
                      isEditMode={isEditMode}
                      onOpen={() => openLightboxAt(index)}
                    />
                  );
                }

                return (
                  <div
                    key={`${index}-${galleryPhotoSrc(photo)}`}
                    className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                    onClick={() => openLightboxAt(index)}
                  >
                    <VibesitesImage
                      url={galleryPhotoSrc(photo)}
                      alt={photoAlt(photo)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Gallery images will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      {/* Lightbox Modal — fixed-size container so all images display consistently */}
      {lightboxOpen && selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button — consistent SVG X icon used across theme (matches VirtualTourEmbed) */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
            aria-label="Close lightbox"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter */}
          {filteredPhotos.length > 1 && selectedIndex !== null && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-20 text-white/80 text-sm font-body tracking-wide">
              {selectedIndex + 1} / {filteredPhotos.length}
            </div>
          )}

          {/* Previous button — consistent chevron icon used in PropertyOverview carousel */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Next button — consistent chevron icon used in PropertyOverview carousel */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Next image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          )}

          {/* Fixed-size image container — consistent dimensions regardless of image aspect ratio */}
          <div
            className="relative w-[90vw] h-[80vh] max-w-5xl flex flex-col items-center justify-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <VibesitesImage
              url={galleryPhotoSrc(selectedPhoto)}
              alt={photoAlt(selectedPhoto)}
              className="max-w-full max-h-[calc(80vh-3rem)] w-auto h-auto object-contain rounded-sm"
              sizes="90vw"
            />
            {caption && (
              <p className="gallery-lightbox-caption max-w-full px-2 text-center text-white/90 text-sm md:text-base font-body leading-snug">
                {caption}
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .gallery-tab:first-child {
          border-radius: 3px 0 0 3px;
        }
        .gallery-tab:last-child {
          border-radius: 0 3px 3px 0;
        }
        .gallery-tab:hover {
          color: var(--vibe-color-text);
          border-color: var(--vibe-color-text-muted);
        }
        .gallery-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .gallery-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }

        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        .gallery-lightbox-caption {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  slotGroups: {
    'gallery.stock_lifestyle_1': stockLifestyle1Def,
    'gallery.stock_lifestyle_2': stockLifestyle2Def,
    'gallery.stock_lifestyle_3': stockLifestyle3Def,
    'gallery.stock_lifestyle_4': stockLifestyle4Def,
    'gallery.stock_lifestyle_5': stockLifestyle5Def,
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as any).contentSchema = contentSchema;

export default Gallery;
